import * as React from 'react';
import {
  DayPicker as BaseDayPicker,
  type DayContentProps,
  type Matcher,
  type DateRange,
} from 'react-day-picker';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import {
  HStack,
  Menu,
  MenuContent,
  MenuGroup,
  MenuTrigger,
  View,
} from '@packages/systems/spring';
import {DatePickerContext} from './DatePickerContext';
import {
  dateRanges,
  dateRangeCollection,
  type DateRangeEntry,
  type UsagePeriodsDataPickerProps,
  timezoneDateRangeCollection,
  useGoToDate,
  offsetRangeForUTC,
} from './__DatePicker.utils';
import {DatePickerMenuItem} from './DatePickerMenuItem';
import {DatePickerCaption} from './DatePickerCaption';
import {DATE_PICKER_TRIGGER_MAX_WIDTH_SITE_PLANS} from './DatePicker.styles';

dayjs.extend(utc);
dayjs.extend(tz);

type MenuProps = React.ComponentPropsWithoutRef<typeof Menu>;
type DatePickerProps = Pick<
  MenuProps,
  | 'isOpen'
  | 'onRequestOpen'
  | 'onRequestClose'
  | 'onOpen'
  | 'onClose'
  | 'shouldAllowPointerEventsOutside'
> & {
  value: {
    from: Date;
    to: Date;
    relativeTimeString: string;
  };
  onChange: (range: {from: Date; to: Date; relativeTimeString: string}) => void;
  disabled?: Matcher;
  maxRangeDays?: number;
  /**
   * When provided, datepicker will display and return dates in the provided timezone. Defaults to UTC otherwise.
   */
  timezone?: string;
  CustomDayContent?: (props: DayContentProps) => JSX.Element;
  // Usage periods are used to display the current billing month, last billing months, and two billing months ago.(max. 3 usage periods)
  usagePeriods?: UsagePeriodsDataPickerProps;
  triggerVariant?: 'solid' | 'outline';
  // Preset-range IDs to omit from the standard ranges.
  excludeRangeIds?: ReadonlyArray<string>;
  /**
   * WFA-2202: when true, the timezone-aware preset ranges use the true UTC
   * instant of each site-local day boundary and clamp "This week/month/quarter"
   * to end-of-today. Defaults to false (legacy behavior).
   */
  enableTimezoneFix?: boolean;
};

/**
 * A date picker component that allows the user to select a date range.
 *
 * The initial value should be from `dateRanges` or created with
 * `createDateRange`.
 *
 * @example
 * ```tsx
 * import {DatePicker, dateRanges} from '@packages/systems/ui/shared-ui/DatePicker';
 *
 * const Example = () => {
 *  const [dateRange, setDateRange] = React.useState(dateRanges.last7Days);
 *
 *   return <DatePicker value={dateRange} onChange={setDateRange} />;
 * };
 * ```
 *
 * @example
 * ```tsx
 * import {DatePicker, createDateRange} from '@packages/systems/ui/shared-ui/DatePicker';
 *
 * const Example = () => {
 *   const initialDateRange = createDateRange(new Date(), new Date());
 *  const [dateRange, setDateRange] = React.useState(initialDateRange);
 *
 *   return <DatePicker value={dateRange} onChange={setDateRange} />;
 * };
 * ```
 */
export const DatePicker = ({
  value = dateRanges.last7Days,
  onChange = () => null,
  disabled,
  maxRangeDays,
  CustomDayContent,
  usagePeriods = [],
  timezone,
  triggerVariant = 'solid',
  excludeRangeIds,
  enableTimezoneFix = false,
  ...props
}: DatePickerProps) => {
  const {goToDate, setGoToDate} = useGoToDate();
  /**
   * Stores (temporary) state, allowing for the user to change the date range
   * to any from/to value.
   *
   * @see https://webflow.atlassian.net/browse/WFA-565
   */
  const [preCommitRange, setPreCommitRange] = React.useState<{
    from: Date;
    to?: Date | undefined;
  } | null>(null);

  const defaultMonth = React.useMemo(() => {
    const utcRange = offsetRangeForUTC(
      {from: value?.from, to: value?.to},
      timezone
    );
    return utcRange?.to ?? utcRange?.from;
  }, [value?.from, value?.to, timezone]);

  const label = value?.relativeTimeString || 'Custom';
  const handleOnClickMenuItem = (item: DateRangeEntry) => (event: Event) => {
    const from = item.from;
    const to = item.to;
    if (!from || !to) {
      return;
    }

    event.stopPropagation();

    const finalFrom = timezone ? from : new Date(from);
    const finalTo = timezone ? to : new Date(to);

    const nextRange = {
      relativeTimeString: item.relativeTimeString,
      from: finalFrom,
      to: finalTo,
    };

    if (goToDate) {
      goToDate(new Date(to));
    }

    onChange(nextRange);
  };

  const baseRanges = timezone
    ? timezoneDateRangeCollection(timezone, enableTimezoneFix)
    : dateRangeCollection;
  const ranges =
    excludeRangeIds && excludeRangeIds.length > 0
      ? baseRanges.filter((range) => !excludeRangeIds.includes(range.id))
      : baseRanges;
  const menuMarkup = (
    <MenuGroup selectionMode="single" value={value?.relativeTimeString}>
      {[...usagePeriods, ...ranges].map((item) => (
        <DatePickerMenuItem
          key={item.relativeTimeString}
          onClick={(event: Event) => handleOnClickMenuItem(item)(event)}
          value={item.relativeTimeString}
        >
          {item.relativeTimeString}
        </DatePickerMenuItem>
      ))}
    </MenuGroup>
  );

  const handleOnChange = (selectedDay: Date) => {
    const isPreCommitComplete = preCommitRange?.from;

    if (isPreCommitComplete) {
      setPreCommitRange(null);

      /**
       * Regardless of the to/from selection order, we have to ensure that the
       * final range has an earlier to date than the from date.
       */
      const isFromBeforeTo = dayjs
        .utc(preCommitRange.from)
        .isBefore(selectedDay);
      const fromValue = isFromBeforeTo ? preCommitRange.from : selectedDay;
      const toValue = isFromBeforeTo ? selectedDay : preCommitRange.from;

      const finalFrom = timezone
        ? dayjs(fromValue).tz(timezone, true).startOf('day').toDate()
        : dayjs.utc(fromValue).startOf('day').toDate();

      const finalTo = timezone
        ? dayjs(toValue).tz(timezone, true).endOf('day').toDate()
        : dayjs.utc(toValue).endOf('day').toDate();

      if (finalFrom && finalTo) {
        const finalRange = {
          from: finalFrom,
          to: finalTo,
          relativeTimeString: 'Custom',
        };

        goToDate(finalTo);

        onChange(finalRange);
      }

      return;
    }

    setPreCommitRange((prev) => {
      if (!prev) {
        return {from: selectedDay, to: undefined};
      }

      return prev;
    });
  };

  const selectedValue =
    preCommitRange ??
    offsetRangeForUTC({from: value?.from, to: value?.to}, timezone);

  // Disable dates beyond the max range
  const disabledDates: Matcher = (date: Date) => {
    if (maxRangeDays && preCommitRange?.from) {
      const maxDate = dayjs(preCommitRange.from)
        .add(maxRangeDays, 'day')
        .toDate();
      const minDate = dayjs(preCommitRange.from)
        .subtract(maxRangeDays, 'day')
        .toDate();
      return date > maxDate || date < minDate;
    }
    return false;
  };

  const datePickerMarkup = (
    <BaseDayPicker
      components={{Caption: DatePickerCaption, DayContent: CustomDayContent}}
      defaultMonth={defaultMonth}
      mode="range"
      onSelect={(nextRange, selectedDay) => {
        if (!nextRange) {
          return;
        }
        handleOnChange(selectedDay);
      }}
      selected={selectedValue as DateRange}
      showOutsideDays
      weekStartsOn={0}
      disabled={[disabled || (() => false), disabledDates]}
    />
  );

  return (
    <Menu height={250} {...props}>
      <View maxWidth={DATE_PICKER_TRIGGER_MAX_WIDTH_SITE_PLANS}>
        <MenuTrigger hasDownArrow isFullWidth variant={triggerVariant}>
          <View as="span" display="flex" textAlign="left">
            {label}
          </View>
        </MenuTrigger>
      </View>
      <MenuContent>
        <DatePickerContext.Provider value={{goToDate, setGoToDate}}>
          <HStack height="100%" align="stretch">
            {menuMarkup}
            {datePickerMarkup}
          </HStack>
        </DatePickerContext.Provider>
      </MenuContent>
    </Menu>
  );
};
